import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import InfoPage from "../InfoPage";
import ModalPortal from "../../components/Modal/ModalPortal";
import { getCloudinaryUrl } from "../../utils/cloudinary";
import { getAuthToken, decodeToken } from "../../utils/authHelpers";
import { displayHandle } from "../../utils/streamHelpers";
import "./Partners.sass";

// Pull a usable Instagram username out of either a full URL stored on the
// partner profile (socials.instagram.url) or a raw handle entered on the
// application form (`@thehostedcreator`, `thehostedcreator`, etc.). Returns
// just the handle text without the leading `@` so it can be passed through
// `displayHandle` for consistent formatting platform-wide.
const extractInstagramHandle = (raw) => {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;
  const urlMatch = value.match(/instagram\.com\/([^/?#]+)/i);
  if (urlMatch && urlMatch[1]) return urlMatch[1];
  return value.replace(/^@+/, '').split(/[/?#]/)[0] || null;
};

// Resolve the @handle shown beneath the partner card. Prefers the IG handle
// from socials, then any raw `instagram` field, and finally falls back to a
// slugified business name so legacy partners don't render anonymously.
const partnerDisplayHandle = (partner) => {
  if (!partner) return '';
  const fromSocials = extractInstagramHandle(partner.socials?.instagram?.url);
  if (fromSocials) return displayHandle(fromSocials);
  const fromInstagram = extractInstagramHandle(partner.instagram);
  if (fromInstagram) return displayHandle(fromInstagram);
  if (partner.business_name) {
    const slug = String(partner.business_name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
    return slug ? `@${slug}` : '';
  }
  return '';
};

// Mirrors the dropdown on the home-page Partner tab. Keep in sync with
// `PARTNER_CATEGORIES` in src/components/Header/index.js,
// `ALLOWED_CATEGORIES` in api/catalogue/partners.mjs and
// `ALLOWED_PARTNER_CATEGORIES` in api/partner/apply.mjs.
export const PARTNER_CATEGORIES = [
  'Marketing',
  'Design',
  'Legal',
  'Mentorship',
  'Real Estate',
  'Amenities',
  'Management',
  'Services',
];

// Normalise a raw `?category=` query value to its canonical label (or null).
const normalizeCategory = (raw) => {
  if (!raw) return null;
  const needle = String(raw).trim().toLowerCase();
  return PARTNER_CATEGORIES.find((c) => c.toLowerCase() === needle) || null;
};

const SocialLink = ({ url, label, svgPath, viewBox = "0 0 24 24" }) => {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" aria-label={label}>
      <svg viewBox={viewBox} fill="currentColor">
        {svgPath}
      </svg>
    </a>
  );
};

const PartnerModal = ({ isOpen, onClose, partner, currentUserId }) => {
  if (!partner) return null;

  const loggedInUserType = typeof window !== 'undefined'
    ? (window.localStorage.getItem('postaway_user_type') || '')
    : '';
  const isSelf = currentUserId && partner.user_id && String(partner.user_id) === String(currentUserId);
  const canMessage = !isSelf && (
    loggedInUserType === 'creator' ||
    loggedInUserType === 'host' ||
    loggedInUserType === 'traveler'
  );

  const optimizedLogo = partner.logo_url
    ? getCloudinaryUrl(partner.logo_url, { width: 800, height: 450, crop: 'fit', quality: 'auto', format: 'auto' }) || partner.logo_url
    : null;

  const locationLine = [partner.location_city, partner.location_state].filter(Boolean).join(', ');
  const socials = partner.socials || {};
  const categories = Array.isArray(partner.categories) ? partner.categories.filter(Boolean) : [];

  const handleMessage = () => {
    if (!partner.user_id) return;
    const params = new URLSearchParams();
    params.set('to', partner.user_id);
    if (partner.business_name) params.set('title', partner.business_name);
    window.location.href = `/messages?${params.toString()}`;
  };

  const handleWebsite = () => {
    if (partner.website_url) {
      window.open(partner.website_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="640px">
      <div className="partner-modal-header">
        {optimizedLogo && (
          <img src={optimizedLogo} alt={partner.business_name || 'Partner logo'} />
        )}
      </div>
      <div className="partner-modal-content">
        <div className="partner-modal-title-section">
          <h2>{partner.business_name}</h2>
          {locationLine && <p>{locationLine}</p>}
          {categories.length > 0 && (
            <div className="partner-modal-categories" aria-label="Categories">
              {categories.map((c) => (
                <span key={c} className="partner-category-tag">{c}</span>
              ))}
            </div>
          )}
        </div>

        {partner.description && (
          <div className="partner-modal-section">
            <h4>About</h4>
            <p>{partner.description}</p>
          </div>
        )}

        {partner.help_text && (
          <div className="partner-modal-section">
            <h4>How we help hosts &amp; creators</h4>
            <p>{partner.help_text}</p>
          </div>
        )}

        {partner.website_url && (
          <div className="partner-modal-section">
            <h4>Website</h4>
            <p>
              <a href={partner.website_url} target="_blank" rel="noopener noreferrer">
                {partner.website_url}
              </a>
            </p>
          </div>
        )}

        {partner.member_discount_code && (
          <div className="partner-modal-discount">
            <p className="partner-discount-label">Postaway Member Discount Code</p>
            <p className="partner-discount-code">{partner.member_discount_code}</p>
          </div>
        )}

        <div className="partner-modal-socials">
          <SocialLink
            url={socials?.instagram?.url}
            label="Instagram"
            svgPath={<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>}
          />
          <SocialLink
            url={socials?.tiktok?.url}
            viewBox="0 0 32 32"
            label="TikTok"
            svgPath={<path d="M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213 1.411 1.271 3.247 2.095 5.271 2.235v5.036c-1.912-0.048-3.71-0.489-5.331-1.247-0.704-0.338-1.347-0.711-1.999-1.162-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.687 4.923-1.652 2.366-4.328 3.919-7.385 4.025-1.865 0.065-3.719-0.381-5.154-1.28-2.508-1.509-4.238-4.091-4.558-7.094-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136 0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-2.235 0-4.055 1.819-4.055 4.055 0 2.235 1.819 4.055 4.055 4.055 2.148 0 3.91-1.68 4.049-3.796 0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z"/>}
          />
          <SocialLink
            url={socials?.youtube?.url}
            viewBox="0 0 20 14"
            label="YouTube"
            svgPath={<path d="M19.615 3.154c-.19-.84-.75-1.5-1.577-1.692C16.264 1.1 10 .9 10 .9S3.736 1.1 2.962 1.462c-.827.192-1.387.852-1.577 1.692C1.1 5.03 1 6.5 1 6.5s.1 1.47.385 3.346c.19.84.75 1.5 1.577 1.692C3.736 11.9 10 12.1 10 12.1s6.264-.2 7.038-.562c.827-.192 1.387-.852 1.577-1.692C18.9 7.97 19 6.5 19 6.5s-.1-1.47-.385-3.346zM8.182 9.182V3.818L13.182 6.5l-5 2.682z"/>}
          />
          <SocialLink
            url={socials?.facebook?.url}
            label="Facebook"
            svgPath={<path d="M22 12a10 10 0 1 0-11.562 9.877V14.89H7.898V12h2.54V9.797c0-2.508 1.493-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46H15.19c-1.242 0-1.629.77-1.629 1.562V12h2.773l-.443 2.89h-2.33v6.987A10.002 10.002 0 0 0 22 12z"/>}
          />
          <SocialLink
            url={socials?.other?.url}
            label="Website"
            svgPath={<path d="M10 14a3.5 3.5 0 0 0 5 0l4-4a3.5 3.5 0 0 0-5-5l-1 1M14 10a3.5 3.5 0 0 0-5 0l-4 4a3.5 3.5 0 0 0 5 5l1-1"/>}
          />
        </div>

        <div className="partner-modal-actions">
          {partner.website_url && (
            <button className="button-website" onClick={handleWebsite}>
              Visit Website
            </button>
          )}
          {canMessage && partner.user_id && (
            <button className="button-message" onClick={handleMessage}>
              Message Partner
            </button>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

const MAX_PARTNER_CATEGORIES = 3;

const emptyApplyForm = {
  businessName: '',
  contactName: '',
  email: '',
  phone: '',
  websiteUrl: '',
  instagram: '',
  locationCity: '',
  locationState: '',
  businessDescription: '',
  helpText: '',
  memberDiscountCode: '',
  categories: [],
};

const PartnerApplyModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(emptyApplyForm);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSubmitting(false);
      setMessage(null);
    }
  }, [isOpen]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleCategory = (category) => {
    setForm((f) => {
      const isSelected = f.categories.includes(category);
      if (isSelected) {
        return { ...f, categories: f.categories.filter((c) => c !== category) };
      }
      if (f.categories.length >= MAX_PARTNER_CATEGORIES) {
        return f;
      }
      return { ...f, categories: [...f.categories, category] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await fetch('/api/partner/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setMessage({ type: 'success', text: "Thanks! Your application has been sent to the Postaway team. We'll be in touch soon." });
        setForm(emptyApplyForm);
      } else {
        throw new Error(data?.error || 'send_failed');
      }
    } catch (err) {
      console.error('Partner apply error:', err);
      setMessage({ type: 'error', text: 'Something went wrong sending your application. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalPortal isOpen={isOpen} onClose={onClose} maxWidth="640px">
      <div className="partner-apply-modal">
        <h3>Apply to be a Partner</h3>
        <p className="partner-apply-subtitle">
          Tell us a bit about your business. We'll review and reach out to get you set up.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="partner-apply-field">
            <label htmlFor="apply-business">Business name<span className="partner-apply-required">*</span></label>
            <input
              id="apply-business"
              type="text"
              required
              value={form.businessName}
              onChange={(e) => update('businessName', e.target.value)}
              placeholder="Glacier Outfitters"
            />
          </div>

          <div className="partner-apply-row">
            <div className="partner-apply-field">
              <label htmlFor="apply-contact">Your name<span className="partner-apply-required">*</span></label>
              <input
                id="apply-contact"
                type="text"
                required
                value={form.contactName}
                onChange={(e) => update('contactName', e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div className="partner-apply-field">
              <label htmlFor="apply-email">Email<span className="partner-apply-required">*</span></label>
              <input
                id="apply-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="jane@example.com"
              />
            </div>
          </div>

          <div className="partner-apply-row">
            <div className="partner-apply-field">
              <label htmlFor="apply-phone">Phone (optional)</label>
              <input
                id="apply-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="partner-apply-field">
              <label htmlFor="apply-website">Website</label>
              <input
                id="apply-website"
                type="url"
                value={form.websiteUrl}
                onChange={(e) => update('websiteUrl', e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div className="partner-apply-row">
            <div className="partner-apply-field">
              <label htmlFor="apply-city">City</label>
              <input
                id="apply-city"
                type="text"
                value={form.locationCity}
                onChange={(e) => update('locationCity', e.target.value)}
                placeholder="Bozeman"
              />
            </div>
            <div className="partner-apply-field">
              <label htmlFor="apply-state">State</label>
              <input
                id="apply-state"
                type="text"
                value={form.locationState}
                onChange={(e) => update('locationState', e.target.value)}
                placeholder="MT"
              />
            </div>
          </div>

          <div className="partner-apply-field">
            <label htmlFor="apply-instagram">
              Instagram handle<span className="partner-apply-required">*</span>
            </label>
            <input
              id="apply-instagram"
              type="text"
              required
              value={form.instagram}
              onChange={(e) => update('instagram', e.target.value)}
              placeholder="@glacieroutfitters"
            />
            <small style={{
              fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
              fontSize: 12,
              color: '#777E90',
            }}>
              Used as your @handle on the Preferred Partners page.
            </small>
          </div>

          <div className="partner-apply-field">
            <label htmlFor="apply-categories">
              Which categories best describe your business?
            </label>
            <p className="partner-apply-hint">
              Choose up to {MAX_PARTNER_CATEGORIES}. These match the categories
              members can browse on the Partners page.
            </p>
            <div
              id="apply-categories"
              className="partner-apply-categories"
              role="group"
              aria-label="Business categories"
            >
              {PARTNER_CATEGORIES.map((category) => {
                const isSelected = form.categories.includes(category);
                const isAtLimit =
                  !isSelected &&
                  form.categories.length >= MAX_PARTNER_CATEGORIES;
                return (
                  <button
                    key={category}
                    type="button"
                    className={`partner-apply-category-chip${
                      isSelected ? ' selected' : ''
                    }${isAtLimit ? ' disabled' : ''}`}
                    aria-pressed={isSelected}
                    disabled={isAtLimit}
                    onClick={() => toggleCategory(category)}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
            <p className="partner-apply-categories-count">
              {form.categories.length}/{MAX_PARTNER_CATEGORIES} selected
            </p>
          </div>

          <div className="partner-apply-field">
            <label htmlFor="apply-description">Tell us about your business</label>
            <textarea
              id="apply-description"
              value={form.businessDescription}
              onChange={(e) => update('businessDescription', e.target.value)}
              placeholder="What do you sell / offer?"
            />
          </div>

          <div className="partner-apply-field">
            <label htmlFor="apply-help">How can you help Postaway hosts &amp; creators?</label>
            <textarea
              id="apply-help"
              value={form.helpText}
              onChange={(e) => update('helpText', e.target.value)}
              placeholder="e.g. product partnerships, gear rentals, bulk pricing, co-marketing."
            />
          </div>

          <div className="partner-apply-field">
            <label htmlFor="apply-discount">Exclusive Postaway member discount code (optional)</label>
            <p className="partner-apply-hint">
              If your business would like to offer an exclusive discount code on
              your services or products to our members, please add that code in
              the field below. Leave blank if you don't have one.
            </p>
            <input
              id="apply-discount"
              type="text"
              value={form.memberDiscountCode}
              onChange={(e) => update('memberDiscountCode', e.target.value)}
              placeholder="e.g. POSTAWAY15"
            />
          </div>

          {message && (
            <div className={`partner-apply-message ${message.type}`}>{message.text}</div>
          )}

          <div className="partner-apply-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-send" disabled={submitting}>
              {submitting ? 'Sending…' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </ModalPortal>
  );
};

const PartnerCard = ({ partner, onClick }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const optimizedLogo = partner.logo_url
    ? getCloudinaryUrl(partner.logo_url, { width: 600, height: 600, crop: 'fill', quality: 'auto', format: 'auto' }) || partner.logo_url
    : null;
  const handleText = partnerDisplayHandle(partner);

  if (!optimizedLogo || imageFailed) return null;

  return (
    <div className="partner-card" onClick={() => onClick(partner)}>
      <div className="partner-image-wrapper">
        <img
          src={optimizedLogo}
          alt={partner.business_name || 'Partner logo'}
          onLoad={() => setIsLoaded(true)}
          onError={() => setImageFailed(true)}
          className={isLoaded ? 'loaded' : ''}
        />
      </div>
      {handleText && (
        <div className="partner-handle">
          <span>{handleText}</span>
        </div>
      )}
    </div>
  );
};

const Partners = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // The active category lives in the URL (`/partners?category=Marketing`) so
  // the Header dropdown can deep-link into a filtered view and the filter
  // survives refresh / back-button.
  const activeCategory = normalizeCategory(searchParams.get('category'));
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [randomSeed, setRandomSeed] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const categoryStripRef = useRef(null);

  // On mobile the chip row is a horizontal scroller; make sure the active
  // chip is visible when arriving via a deep link like ?category=Services.
  useEffect(() => {
    const strip = categoryStripRef.current;
    if (!strip) return;
    const active = strip.querySelector('.partners-category-chip.active');
    if (!active) return;
    if (strip.scrollWidth <= strip.clientWidth) return;
    const stripRect = strip.getBoundingClientRect();
    const chipRect = active.getBoundingClientRect();
    const target = strip.scrollLeft + (chipRect.left - stripRect.left) - (stripRect.width - chipRect.width) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [activeCategory]);

  // Grab current user id when someone is signed in. The directory itself is
  // public; this only powers "message this partner" for logged-in members.
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      try {
        const decoded = decodeToken(token);
        if (decoded?.userId) setCurrentUserId(decoded.userId);
      } catch (_) {}
    }
  }, []);

  const fetchPartners = async (pageNum, search = '', seedOverride, categoryOverride) => {
    setLoading(true);
    try {
      // Reuse the seed across pagination so the random shuffle stays stable
      // within a session; on a fresh page load the state is null so the
      // server picks a new seed → cards reshuffle on reload.
      const seedToUse = seedOverride !== undefined ? seedOverride : randomSeed;
      const categoryToUse = categoryOverride !== undefined ? categoryOverride : activeCategory;
      const response = await fetch('/api/catalogue/partners', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: pageNum,
          limit: 24,
          search,
          sort: 'random',
          seed: seedToUse,
          category: categoryToUse || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (typeof data.seed === 'number') {
          setRandomSeed(data.seed);
        }
        if (pageNum === 1) {
          setPartners(data.partners || []);
        } else {
          setPartners(prev => [...prev, ...(data.partners || [])]);
        }
        setHasMore(Boolean(data.hasMore));
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load + refetch whenever the URL category changes (e.g. arriving
  // from the Header dropdown or tapping a filter chip).
  useEffect(() => {
    setPage(1);
    setRandomSeed(null);
    fetchPartners(1, searchTerm, null, activeCategory);
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchInput === searchTerm) return;
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
      setRandomSeed(null);
      fetchPartners(1, searchInput, null);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    setPage(next);
    fetchPartners(next, searchTerm);
  };

  const handleCategorySelect = (category) => {
    const next = category === activeCategory ? null : category;
    const params = new URLSearchParams(searchParams);
    if (next) params.set('category', next);
    else params.delete('category');
    setSearchParams(params, { replace: true });
  };

  const handleCardClick = (partner) => {
    setSelectedPartner(partner);
    setModalOpen(true);
  };

  // Deep-link: open a specific partner's modal from ?partnerId= (used by the
  // "new Preferred Partner joined" notifications). Mirrors the /creators
  // ?profileId= flow: use the loaded card if present, otherwise fetch the
  // single record so it opens even when it isn't on the current page.
  const handledPartnerDeepLinkRef = useRef(false);
  useEffect(() => {
    if (handledPartnerDeepLinkRef.current) return;

    const partnerId = searchParams.get('partnerId');
    if (!partnerId) return;

    handledPartnerDeepLinkRef.current = true;

    const existing = partners.find((p) => String(p.id) === String(partnerId));
    if (existing) {
      handleCardClick(existing);
      return;
    }

    (async () => {
      try {
        const response = await fetch('/api/catalogue/partners', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: partnerId }),
        });
        if (response.ok) {
          const data = await response.json();
          const partner = (data.partners || [])[0];
          if (partner) handleCardClick(partner);
        }
      } catch (err) {
        console.error('Error opening partner from deep link:', err);
      }
    })();
  }, [partners, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
    setRandomSeed(null);
    fetchPartners(1, '', null);
  };

  const content = (
    <div className="partners-page">
      <div className="partners-container">
        <div className="partners-header">
          <div className="partners-title">Preferred Partners</div>
          <div className="partners-description">
            <p>
              Trusted businesses offering exclusive perks and discounts to the Postaway community.
            </p>

            <div className="partners-apply-cta">
              <button type="button" onClick={() => setApplyModalOpen(true)}>
                Apply to be a Partner
              </button>
            </div>

            <div className="partners-search-container">
              <div className="partners-search-wrapper">
                <input
                  type="text"
                  placeholder="Search partners by name or location..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="partners-search-input"
                />
                {searchInput && (
                  <button onClick={handleClearSearch} className="partners-search-clear" aria-label="Clear search">
                    ×
                  </button>
                )}
              </div>
            </div>

            <div
              ref={categoryStripRef}
              className="partners-category-filter"
              role="group"
              aria-label="Filter partners by category"
            >
              <button
                type="button"
                className={`partners-category-chip${!activeCategory ? ' active' : ''}`}
                aria-pressed={!activeCategory}
                onClick={() => handleCategorySelect(null)}
              >
                All
              </button>
              {PARTNER_CATEGORIES.map((category) => (
                <button
                  key={category}
                  type="button"
                  className={`partners-category-chip${activeCategory === category ? ' active' : ''}`}
                  aria-pressed={activeCategory === category}
                  onClick={() => handleCategorySelect(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            {searchTerm && (
              <p className="partners-search-info">Searching for "{searchTerm}"</p>
            )}
            {total > 0 && (
              <p className="partners-search-info">
                Showing {partners.length} of {total}{activeCategory ? ` ${activeCategory}` : ''} partners
              </p>
            )}
          </div>

          <div className="partners-grid-container">
            <div className="partners-grid">
              {partners.map((p) => (
                <PartnerCard
                  key={p.id}
                  partner={p}
                  onClick={handleCardClick}
                />
              ))}
            </div>
            {!loading && partners.length === 0 && (
              <div className="partners-empty">
                {searchTerm ? (
                  <p>No partners match "{searchTerm}"{activeCategory ? ` in ${activeCategory}` : ''}.</p>
                ) : activeCategory ? (
                  <p>{activeCategory} partners coming soon</p>
                ) : (
                  <p>Coming Soon</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="partners-loading"><div>Loading...</div></div>
      )}

      {hasMore && !loading && partners.length > 0 && (
        <div className="partners-load-more">
          <button onClick={handleLoadMore}>Load More Partners</button>
        </div>
      )}

      {!hasMore && partners.length > 0 && (
        <div className="partners-end-message">
          <p>You've reached the end of our partner directory</p>
        </div>
      )}

      <PartnerModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedPartner(null); }}
        partner={selectedPartner}
        currentUserId={currentUserId}
      />

      <PartnerApplyModal
        isOpen={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
      />
    </div>
  );

  return <InfoPage below={content} disableStamps customMinHeight={800} showInlineLivePulse />;
};

export default Partners;
